import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, XCircle, RefreshCw, MapPin, Bike, Car, Zap, FileText, Loader2, Trash2, Award, Pencil, Search, Truck, CheckSquare, Square, X, Building2, Plus, Map } from 'lucide-react';
import { adminDriverApi, adminApi } from '../services/api';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import TierBadge from '../components/TierBadge';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-rose-50 text-rose-700',
};

// Bike / Scooter riders are billed as "couriers" in ShipitNow's product model;
// Car / Van / Truck are motorized "drivers". Anything else defaults to driver.
const COURIER_VEHICLES = new Set(['bike', 'scooter', 'bicycle', 'foot', 'walk']);
const isCourier = (d) => COURIER_VEHICLES.has(String(d?.vehicle_type || '').trim().toLowerCase());

const DriversAdminPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [kind, setKind] = useState('all'); // all | driver | courier
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [docsDialog, setDocsDialog] = useState({ open: false, driver: null });
  const [editDialog, setEditDialog] = useState({ open: false, driver: null });
  const [zones, setZones] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [zoneBulkDialog, setZoneBulkDialog] = useState({ open: false, mode: 'add', zone_ids: [], busy: false });

  useEffect(() => {
    api.get('/zones').then((r) => setZones(r.data || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try { setDrivers(await adminDriverApi.list(filter === 'all' ? undefined : filter)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return drivers.filter((d) => {
      if (kind === 'courier' && !isCourier(d)) return false;
      if (kind === 'driver' && isCourier(d)) return false;
      if (!needle) return true;
      const hay = [
        d.name, d.email, d.phone, d.vehicle_type, d.vehicle_plate,
        d.service_area, d.home_city, d.driver_id, d.postal_code,
        ...((d.zone_chips || []).map((z) => z.name)),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [drivers, kind, q]);

  const counts = useMemo(() => ({
    all: drivers.length,
    driver: drivers.filter((d) => !isCourier(d)).length,
    courier: drivers.filter((d) => isCourier(d)).length,
  }), [drivers]);

  // -------- Bulk selection helpers --------
  const filteredIds = useMemo(() => filtered.map((d) => d.driver_id), [filtered]);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelected((prev) => {
    if (allSelected) {
      const next = new Set(prev);
      filteredIds.forEach((id) => next.delete(id));
      return next;
    }
    return new Set([...prev, ...filteredIds]);
  });
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action, verb) => {
    const ids = [...selected];
    if (ids.length === 0) { toast.error('Nothing selected'); return; }
    if (action === 'delete' && !window.confirm(`Permanently delete ${ids.length} driver(s)? In-flight orders will be re-broadcast.`)) return;
    setBulkBusy(true);
    try {
      const res = await adminApi.bulkDrivers(ids, action);
      const ok = res?.counts?.succeeded ?? 0;
      const fail = res?.counts?.failed ?? 0;
      const suffix = res?.orders_reset ? ` · ${res.orders_reset} order(s) re-broadcast` : '';
      toast.success(`${verb} ${ok}${fail ? ` · ${fail} failed` : ''}${suffix}`);
      if (fail && res?.errors) {
        const firstErr = Object.entries(res.errors)[0];
        if (firstErr) toast.error(`${firstErr[0].slice(0, 12)}…: ${firstErr[1]}`);
      }
      clearSelection();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk action failed'); }
    finally { setBulkBusy(false); }
  };

  const setStatus = async (id, status) => {
    try { await adminDriverApi.update(id, { status }); toast.success(`Driver ${status}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const removeDriver = async (d) => {
    if (!window.confirm(`Permanently delete ${d.name || 'this driver'}? Any in-flight orders will be un-assigned and re-broadcast.`)) return;
    try {
      const r = await adminDriverApi.remove(d.driver_id);
      toast.success(`Driver deleted${r?.orders_reset ? ` · ${r.orders_reset} order(s) re-broadcast` : ''}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };

  const toggleCourier = async (d) => {
    const now = !d.is_courier_promoted;
    try {
      await adminDriverApi.update(d.driver_id, { is_courier_promoted: now });
      toast.success(now
        ? `${d.name} promoted to COURIER — they can now take same-day package, courier & business-logistics jobs.`
        : `${d.name} is no longer a courier.`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const toggleSuper = async (d) => {
    const now = !d.is_super_driver;
    try {
      const doc = await adminDriverApi.update(d.driver_id, { is_super_driver: now });
      toast.success(now
        ? `${d.name} is now a Super Driver — they can pick orders city-wide.`
        : `${d.name} is no longer a Super Driver.`);
      if (doc?._admin_wa_link) {
        toast(`Let ${d.name} know on WhatsApp?`, {
          duration: 8000,
          action: { label: 'Open WhatsApp', onClick: () => window.open(doc._admin_wa_link, '_blank', 'noopener') },
        });
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Drivers &amp; Couriers</h1>
          <p className="text-slate-500 mt-1">
            Approve, edit and remove motorized drivers <span className="text-slate-400">(Car / Van / Truck)</span> and bike couriers <span className="text-slate-400">(Bike / Scooter)</span> from one list.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, phone, plate, zone…"
              className="pl-9 h-10 w-72"
              data-testid="drivers-search-input"
            />
          </div>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-44 h-10" data-testid="drivers-kind-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ({counts.all})</SelectItem>
              <SelectItem value="driver">Drivers ({counts.driver})</SelectItem>
              <SelectItem value="courier">Couriers ({counts.courier})</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {selected.size > 0 && (
          <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-900 text-white flex items-center gap-2 flex-wrap" data-testid="drivers-bulk-toolbar">
            <span className="text-sm font-semibold">{selected.size} selected</span>
            <button type="button" onClick={clearSelection} className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1" data-testid="drivers-bulk-clear">
              <X className="h-3.5 w-3.5" />Clear
            </button>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="secondary" disabled={bulkBusy} onClick={() => setZoneBulkDialog({ open: true, mode: 'add', zone_ids: [], busy: false })} data-testid="drivers-bulk-zones">
                <Map className="h-3.5 w-3.5 mr-1" />Assign zones
              </Button>
              <Button size="sm" variant="secondary" disabled={bulkBusy} onClick={() => runBulk('approve', 'Approved')} data-testid="drivers-bulk-approve">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve
              </Button>
              <Button size="sm" variant="secondary" disabled={bulkBusy} onClick={() => runBulk('reject', 'Rejected')} data-testid="drivers-bulk-reject">
                <XCircle className="h-3.5 w-3.5 mr-1" />Reject
              </Button>
              <Button size="sm" variant="secondary" disabled={bulkBusy} onClick={() => runBulk('set_offline', 'Sent offline')} data-testid="drivers-bulk-offline">
                Set offline
              </Button>
              <Button size="sm" disabled={bulkBusy} onClick={() => runBulk('delete', 'Deleted')} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="drivers-bulk-delete">
                {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}Delete
              </Button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="w-10 px-3 py-3">
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    disabled={filteredIds.length === 0}
                    className="text-slate-500 hover:text-slate-900 disabled:opacity-40"
                    data-testid="drivers-select-all"
                    title={allSelected ? 'Clear selection' : 'Select all visible drivers'}
                  >
                    {allSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                  </button>
                </th>
                <th className="text-left font-medium px-4 py-3">Driver</th>
                <th className="text-left font-medium px-4 py-3">Vehicle</th>
                <th className="text-left font-medium px-4 py-3">Service area</th>
                <th className="text-left font-medium px-4 py-3">Activity</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-right font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 ? (
                <tr><td colSpan="7" className="text-center text-slate-500 py-10" data-testid="drivers-empty-state">{drivers.length === 0 ? 'No drivers found.' : 'No matches for this search / filter.'}</td></tr>
              ) : filtered.map((d) => (
                <tr key={d.driver_id} className={`hover:bg-slate-50 ${selected.has(d.driver_id) ? 'bg-slate-50' : ''}`} data-testid={`driver-row-${d.driver_id}`}>
                  <td className="px-3 py-3">
                    <button
                      type="button"
                      onClick={() => toggleSelect(d.driver_id)}
                      className="text-slate-500 hover:text-slate-900"
                      data-testid={`drivers-select-${d.driver_id}`}
                    >
                      {selected.has(d.driver_id) ? <CheckSquare className="h-4 w-4 text-emerald-600" /> : <Square className="h-4 w-4" />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {d.photo ? <img src={d.photo} alt="" className="h-8 w-8 rounded-full object-cover" /> : (
                        <div className="h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">{(d.name || 'D').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase()}</div>
                      )}
                      <div>
                        <div className="font-medium text-slate-900 flex items-center gap-1.5 flex-wrap">
                          {d.name}
                          {d.is_company_partner && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-indigo-600 text-white px-1.5 py-0.5 rounded-full"
                              data-testid={`kind-badge-partner-${d.driver_id}`}
                              title="Partner courier company — admin-created placeholder"
                            >
                              <Building2 className="h-2.5 w-2.5" /> Partner
                            </span>
                          )}
                          {isCourier(d) ? (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded-full"
                              data-testid={`kind-badge-courier-${d.driver_id}`}
                              title="Courier — Bike / Scooter"
                            >
                              <Bike className="h-2.5 w-2.5" /> Courier
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded-full"
                              data-testid={`kind-badge-driver-${d.driver_id}`}
                              title="Driver — Car / Van / Truck"
                            >
                              <Car className="h-2.5 w-2.5" /> Driver
                            </span>
                          )}
                          <TierBadge tier={d.tier_badge || d.tier} />
                          {d.is_courier && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-indigo-600 text-white px-1.5 py-0.5 rounded-full shadow"
                              data-testid={`courier-badge-${d.driver_id}`}
                              title="COURIER — verified registered business + insurance; eligible for same-day packages, courier & business logistics"
                            >
                              COURIER
                            </span>
                          )}
                          {d.is_super_driver && d.tier !== 'super' && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-lime-400 text-emerald-900 px-1.5 py-0.5 rounded-full shadow"
                              data-testid={`super-driver-badge-${d.driver_id}`}
                              title="Super Driver — can pick orders city-wide, including stalled (>20 min) orders"
                            >
                              <Zap className="h-2.5 w-2.5" /> Super
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">{d.email} · {d.phone}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {(d.deliveries_count ?? 0)} deliveries · {(d.rating ?? 0).toFixed(2)}★ · payout {(d.payout_share != null ? (d.payout_share * 100).toFixed(1) : (65 + ['bronze','silver','gold','platinum','super'].indexOf(d.tier || 'bronze') * 2.5).toFixed(1))}%
                        </div>
                        {(d.zone_chips || []).length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1" data-testid={`zone-chips-${d.driver_id}`}>
                            {(d.zone_chips || []).slice(0, 5).map((z) => (
                              <span
                                key={z.id}
                                title={`${z.name} — ${z.city}`}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium"
                                style={{ backgroundColor: `${z.color}20`, color: z.color, border: `1px solid ${z.color}40` }}
                              >
                                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: z.color }}></span>
                                {z.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <span className="inline-flex items-center gap-1.5">
                      {isCourier(d) ? <Bike className="h-3.5 w-3.5 text-sky-600" /> : (String(d.vehicle_type || '').toLowerCase().includes('truck') ? <Truck className="h-3.5 w-3.5 text-indigo-600" /> : <Car className="h-3.5 w-3.5 text-indigo-600" />)}
                      {d.vehicle_type || '—'}
                    </span>
                    <span className="text-slate-400"> · {d.vehicle_plate || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-700"><MapPin className="h-3.5 w-3.5 inline text-slate-400 mr-1" />{d.service_area}</td>
                  <td className="px-4 py-3 text-slate-600"><Bike className="h-3.5 w-3.5 inline text-slate-400 mr-1" />{d.deliveries_count || 0} deliveries</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 flex-wrap">
                      <Badge className={`${STATUS_COLORS[d.status] || 'bg-slate-100'} border-0 hover:opacity-100 capitalize`}>{d.status}</Badge>
                      {d.is_online && <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white border-0">Online</Badge>}
                      {d.is_busy && <Badge className="bg-amber-500 hover:bg-amber-500 text-white border-0">Busy</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {d.status === 'approved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDocsDialog({ open: true, driver: d })}
                        className="mr-2"
                        data-testid={`view-docs-${d.driver_id}`}
                        title="View this driver's uploaded license, registration, insurance and profile photo"
                      >
                        <FileText className="h-3.5 w-3.5 mr-1" />
                        Docs
                      </Button>
                    )}
                    {d.status === 'approved' && d.super_driver_application_status === 'pending' && !d.is_super_driver && (
                      <Button
                        size="sm"
                        onClick={async () => {
                          try {
                            await adminDriverApi.update(d.driver_id, { is_super_driver: true, super_driver_application_status: 'approved' });
                            toast.success(`${d.name}'s Super Driver application approved.`);
                            load();
                          } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
                        }}
                        className="bg-lime-500 hover:bg-lime-600 text-emerald-900 font-semibold mr-2"
                        data-testid={`approve-super-application-${d.driver_id}`}
                        title="Driver applied to become Super Driver"
                      >
                        <Zap className="h-3.5 w-3.5 mr-1" />
                        Approve Super
                      </Button>
                    )}
                    {d.status === 'approved' && !d.super_driver_application_status && (
                      <Button
                        size="sm"
                        variant={d.is_super_driver ? 'default' : 'outline'}
                        onClick={() => toggleSuper(d)}
                        className={d.is_super_driver
                          ? 'bg-lime-400 hover:bg-lime-500 text-emerald-900 font-semibold mr-2'
                          : 'text-emerald-700 border-emerald-200 hover:bg-emerald-50 mr-2'}
                        data-testid={`toggle-super-driver-${d.driver_id}`}
                        title="Super drivers can pick orders anywhere in the city, especially stalled orders (>20 min)"
                      >
                        <Zap className="h-3.5 w-3.5 mr-1" />
                        {d.is_super_driver ? 'Super ✓' : 'Make super'}
                      </Button>
                    )}
                    {d.status === 'approved' && (
                      <Button
                        size="sm"
                        variant={d.is_courier_promoted ? 'default' : 'outline'}
                        onClick={() => toggleCourier(d)}
                        className={d.is_courier_promoted
                          ? 'bg-purple-600 hover:bg-purple-700 text-white mr-2'
                          : 'text-purple-700 border-purple-200 hover:bg-purple-50 mr-2'}
                        data-testid={`toggle-courier-${d.driver_id}`}
                        title="Couriers can take same-day package, courier and business-logistics jobs"
                      >
                        <Truck className="h-3.5 w-3.5 mr-1" />
                        {d.is_courier_promoted ? 'Courier ✓' : 'Promote to courier'}
                      </Button>
                    )}
                    {d.status === 'approved' && (
                      <Select
                        value={d.tier_override || 'auto'}
                        onValueChange={async (v) => {
                          const payload = { tier_override: v === 'auto' ? '' : v };
                          try { await adminDriverApi.update(d.driver_id, payload); toast.success('Tier updated'); load(); }
                          catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
                        }}
                      >
                        <SelectTrigger className="h-8 w-32 mr-2 text-xs" data-testid={`tier-select-${d.driver_id}`}>
                          <Award className="h-3 w-3 mr-1" />
                          <SelectValue placeholder="Tier" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto</SelectItem>
                          <SelectItem value="bronze">Bronze</SelectItem>
                          <SelectItem value="silver">Silver</SelectItem>
                          <SelectItem value="gold">Gold</SelectItem>
                          <SelectItem value="platinum">Platinum</SelectItem>
                          <SelectItem value="super">Super</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {d.status !== 'approved' && (
                      <Button size="sm" onClick={() => setStatus(d.driver_id, 'approved')} className="bg-emerald-600 hover:bg-emerald-700 text-white mr-2"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve</Button>
                    )}
                    {d.status !== 'rejected' && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(d.driver_id, 'rejected')} className="text-rose-600 hover:text-rose-700 border-rose-200 mr-2"><XCircle className="h-3.5 w-3.5 mr-1" />Reject</Button>
                    )}
                    {d.status === 'rejected' && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(d.driver_id, 'pending')}>Reset to pending</Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditDialog({ open: true, driver: d })}
                      className="mr-2"
                      data-testid={`driver-edit-${d.driver_id}`}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeDriver(d)}
                      className="text-rose-600 hover:text-rose-700 border-rose-200 ml-2"
                      data-testid={`driver-delete-${d.driver_id}`}
                      title="Permanently delete this driver"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={docsDialog.open} onOpenChange={(o) => !o && setDocsDialog({ open: false, driver: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" />
              Documents · {docsDialog.driver?.name}
            </DialogTitle>
            <DialogDescription>
              Read-only copies of every document this driver uploaded. ShipitNow retains these on file.
            </DialogDescription>
          </DialogHeader>
          {docsDialog.driver && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="admin-driver-docs-grid">
              {[
                ['photo', 'Profile photo'],
                ['license_photo_url', 'License (front)'],
                ['license_photo_back_url', 'License (back)'],
                ['registration_photo_url', 'Vehicle registration'],
                ['insurance_doc_url', 'Insurance document'],
              ].map(([k, label]) => {
                const url = docsDialog.driver[k];
                return (
                  <div key={k} className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden" data-testid={`admin-doc-${k}`}>
                    <div className="px-3 pt-2.5 pb-1.5 text-xs font-semibold text-slate-600 flex items-center justify-between">
                      <span>{label}</span>
                      {url && (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[10px] uppercase tracking-wider text-emerald-700 hover:text-emerald-800"
                          data-testid={`admin-doc-download-${k}`}
                        >
                          Open / download
                        </a>
                      )}
                    </div>
                    {url ? (
                      <img src={url} alt={label} className="w-full aspect-[4/3] object-cover" />
                    ) : (
                      <div className="w-full aspect-[4/3] flex items-center justify-center text-xs text-slate-400 italic">
                        Not uploaded
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="col-span-full text-xs text-slate-500 border-t border-slate-100 pt-3">
                Home address: <span className="font-medium text-slate-800">{docsDialog.driver.home_address || '—'}</span>
                {docsDialog.driver.postal_code && <> · <span className="font-mono text-slate-700">{docsDialog.driver.postal_code}</span></>}
                {docsDialog.driver.home_city && <> · {docsDialog.driver.home_city}</>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EditDriverDialog
        open={editDialog.open}
        driver={editDialog.driver}
        zones={zones}
        onClose={() => setEditDialog({ open: false, driver: null })}
        onSaved={() => { setEditDialog({ open: false, driver: null }); load(); }}
      />

      <AddCourierCompanyDialog
        open={addCompanyOpen}
        onClose={() => setAddCompanyOpen(false)}
        onSaved={() => { setAddCompanyOpen(false); load(); }}
      />

      <BulkZonesDialog
        state={zoneBulkDialog}
        onChange={setZoneBulkDialog}
        zones={zones}
        selectedIds={[...selected]}
        onDone={() => { setZoneBulkDialog({ open: false, mode: 'add', zone_ids: [], busy: false }); clearSelection(); load(); }}
      />
    </div>
  );
};

const EditDriverDialog = ({ open, driver, zones, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (driver) {
      setForm({
        name: driver.name || '',
        phone: driver.phone || '',
        home_city: driver.home_city || 'Ottawa',
        deliveries_count: driver.deliveries_count ?? 0,
        rating: driver.rating ?? 5.0,
        zone_ids: driver.zone_ids || [],
      });
    }
  }, [driver]);
  if (!driver) return null;
  const cityZones = zones.filter((z) => z.city === (form.home_city || driver.home_city));
  const toggleZone = (id) => {
    setForm((f) => {
      const has = (f.zone_ids || []).includes(id);
      let next = has ? f.zone_ids.filter((x) => x !== id) : [...(f.zone_ids || []), id];
      if (next.length > 5) next = next.slice(0, 5);
      return { ...f, zone_ids: next };
    });
  };
  const save = async () => {
    setSaving(true);
    try {
      await adminDriverApi.update(driver.driver_id, {
        name: form.name || undefined,
        phone: form.phone || undefined,
        home_city: form.home_city || undefined,
        deliveries_count: Number(form.deliveries_count) || 0,
        rating: Number(form.rating) || 0,
        zone_ids: form.zone_ids,
      });
      toast.success('Driver saved');
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit driver</DialogTitle>
          <DialogDescription>Update name, phone, stats, and pickup zones (max 5).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-driver-name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Phone</label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-driver-phone" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">City</label>
              <Select value={form.home_city} onValueChange={(v) => setForm({ ...form, home_city: v, zone_ids: [] })}>
                <SelectTrigger data-testid="edit-driver-city"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ottawa">Ottawa</SelectItem>
                  <SelectItem value="Ibadan">Ibadan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Deliveries</label>
              <Input type="number" min="0" value={form.deliveries_count} onChange={(e) => setForm({ ...form, deliveries_count: e.target.value })} data-testid="edit-driver-deliveries" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Rating</label>
              <Input type="number" step="0.05" min="0" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="edit-driver-rating" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pickup zones ({(form.zone_ids || []).length}/5)</label>
              <span className="text-[11px] text-slate-500">Tap to toggle</span>
            </div>
            <div className="flex flex-wrap gap-1.5 p-2 rounded-lg border border-slate-200 bg-slate-50 max-h-56 overflow-y-auto" data-testid="edit-driver-zones">
              {cityZones.length === 0 ? (
                <span className="text-xs text-slate-400 italic">No zones for {form.home_city}.</span>
              ) : cityZones.map((z) => {
                const on = (form.zone_ids || []).includes(z.id);
                return (
                  <button
                    key={z.id}
                    type="button"
                    onClick={() => toggleZone(z.id)}
                    data-testid={`edit-driver-zone-${z.id}`}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition ${on ? '' : 'opacity-60 hover:opacity-90'}`}
                    style={{ backgroundColor: on ? z.color : `${z.color}20`, color: on ? '#fff' : z.color, border: `1px solid ${z.color}` }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: on ? '#fff' : z.color }}></span>
                    {z.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="edit-driver-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save changes'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AddCourierCompanyDialog = ({ open, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: '', home_city: 'Ottawa', vehicle_type: 'bike', phone: '', email: '', contact_website: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({ name: '', home_city: 'Ottawa', vehicle_type: 'bike', phone: '', email: '', contact_website: '', notes: '' });
  }, [open]);
  const save = async () => {
    if (!form.name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/couriers/companies', {
        name: form.name.trim(),
        home_city: form.home_city.trim() || 'Ottawa',
        vehicle_type: form.vehicle_type,
        phone: form.phone || undefined,
        email: form.email || undefined,
        contact_website: form.contact_website || undefined,
        notes: form.notes || undefined,
      });
      toast.success(`${form.name} added to the couriers list`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add courier company'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-600" />
            Add courier company
          </DialogTitle>
          <DialogDescription>
            Register a partner brand (e.g. Swift Courier Brooklyn). It appears in the drivers list with a &quot;Partner&quot; badge. Not geofenced.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Company name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Swift Courier Brooklyn" data-testid="add-company-name" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">City / market</label>
              <Input value={form.home_city} onChange={(e) => setForm({ ...form, home_city: e.target.value })} placeholder="Brooklyn" data-testid="add-company-city" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Vehicle</label>
              <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                <SelectTrigger data-testid="add-company-vehicle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bike">Bike (courier)</SelectItem>
                  <SelectItem value="scooter">Scooter (courier)</SelectItem>
                  <SelectItem value="car">Car (driver)</SelectItem>
                  <SelectItem value="van">Van (driver)</SelectItem>
                  <SelectItem value="truck">Truck (driver)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Phone (optional)</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1718…" data-testid="add-company-phone" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact email (optional)</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ops@swiftcourier.com" data-testid="add-company-email" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Website (optional)</label>
            <Input value={form.contact_website} onChange={(e) => setForm({ ...form, contact_website: e.target.value })} placeholder="https://…" data-testid="add-company-website" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Internal notes (optional)</label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="How to dispatch, cutoff times, etc." data-testid="add-company-notes" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-company-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" />Add company</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BulkZonesDialog = ({ state, onChange, zones, selectedIds, onDone }) => {
  const setState = (patch) => onChange({ ...state, ...patch });
  const toggleZone = (zid) => {
    const has = state.zone_ids.includes(zid);
    setState({ zone_ids: has ? state.zone_ids.filter((x) => x !== zid) : [...state.zone_ids, zid] });
  };
  const apply = async () => {
    if (!state.zone_ids.length && state.mode !== 'remove') {
      toast.error('Pick at least one zone');
      return;
    }
    if (!selectedIds.length) { toast.error('No drivers selected'); return; }
    setState({ busy: true });
    try {
      const res = await adminApi.bulkDriverZones(selectedIds, state.zone_ids, state.mode);
      const ok = res?.counts?.succeeded ?? 0;
      const fail = res?.counts?.failed ?? 0;
      const verbMap = { add: 'Attached zones to', replace: 'Replaced zones on', remove: 'Removed zones from' };
      toast.success(`${verbMap[state.mode]} ${ok} driver${ok === 1 ? '' : 's'}${fail ? ` · ${fail} skipped` : ''}`);
      if (fail && res?.errors) {
        const first = Object.entries(res.errors)[0];
        if (first) toast.error(`${first[0].slice(0, 12)}…: ${first[1]}`);
      }
      // Close the dialog + reset picks BEFORE calling onDone so the "N drivers"
      // header can't flash to "0 drivers" while the parent clears selection.
      onChange({ open: false, mode: 'add', zone_ids: [], busy: false });
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk zone update failed');
      setState({ busy: false });
    }
  };

  const modeDescriptions = {
    add: "Adds these zones to each selected driver's current list, skipping any that would exceed the 5-zone cap.",
    replace: "Overwrites each selected driver's zone list with exactly the zones you pick here.",
    remove: 'Removes these zones from each selected driver. Leaves their other zones untouched.',
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onChange({ ...state, open: false })}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Map className="h-5 w-5 text-emerald-600" />
            Assign zones to {selectedIds.length} driver{selectedIds.length === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription>
            Rebalance coverage in one sweep. Cap is 5 zones per driver.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Action</label>
            <Select value={state.mode} onValueChange={(v) => setState({ mode: v })}>
              <SelectTrigger data-testid="bulk-zones-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="add">Add (append to current)</SelectItem>
                <SelectItem value="replace">Replace (overwrite)</SelectItem>
                <SelectItem value="remove">Remove (detach)</SelectItem>
              </SelectContent>
            </Select>
            <div className="mt-1 text-[11px] text-slate-500">{modeDescriptions[state.mode]}</div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Zones ({state.zone_ids.length} picked)</label>
              {state.zone_ids.length > 0 && (
                <button type="button" className="text-[11px] text-slate-500 hover:text-slate-800" onClick={() => setState({ zone_ids: [] })}>Clear</button>
              )}
            </div>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="bulk-zones-list">
              {zones.length === 0 ? (
                <div className="p-3 text-sm text-slate-500 text-center">No zones defined.</div>
              ) : zones.map((z) => {
                const on = state.zone_ids.includes(z.id);
                return (
                  <button
                    type="button"
                    key={z.id}
                    onClick={() => toggleZone(z.id)}
                    data-testid={`bulk-zones-pick-${z.id}`}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition ${on ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: z.color || '#64748b' }} />
                      <span className="text-sm text-slate-800 truncate">{z.name}</span>
                      <span className="text-[11px] text-slate-400 shrink-0">{z.city}</span>
                    </div>
                    {on ? <CheckSquare className="h-4 w-4 text-emerald-600" /> : <Square className="h-4 w-4 text-slate-300" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onChange({ ...state, open: false })}>Cancel</Button>
          <Button onClick={apply} disabled={state.busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bulk-zones-apply">
            {state.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DriversAdminPage;